import React, { useEffect, useState, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';

import {
  DropDownMenuProps,
  Divider,
  Autocomplete,
  Select,
  Typography,
  Grid,
  type AutocompleteOptionProps
} from '@p81-common/ui';
import { NoResults, ResultItem } from './components';
import createClasses from './styles';
import { useTheme } from '@mui/styles';
import { GlobalSearchProps, ResultItemProps } from './types';
import { useTranslation } from 'react-i18next';

const MIN_CHARACTER_LENGTH = 3;
const pageSize = 100;

export const GlobalSearch = (props: GlobalSearchProps) => {
  const {
    selectOptions,
    handleTextChange,
    results,
    loading,
    setSearchPlaceholder,
    onCategoryChange,
    totalRecords = 0
  } = props;

  const { t } = useTranslation('globalSearch');

  const [isActive, setIsActive] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [isPendingSearch, setIsPendingSearch] = useState(false);
  const [selectValue, setSelectValue] = useState(selectOptions[0]?.value);

  const theme = useTheme();
  const inputBorderColor = isActive
    ? `${theme.palette.secondary[500]} !important`
    : theme.palette.primary[200];

  const classes = createClasses({ inputBorderColor });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const hasFocusedClass = document.querySelector('.Mui-focused');
      setIsActive(!!hasFocusedClass);
    });

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!loading) {
      setIsPendingSearch(false);
    }
  }, [loading]);

  const handleSelectChange: NonNullable<DropDownMenuProps['handleClick']> =
    name => () => {
      setSelectValue(
        selectOptions.find(option => option.name === name)?.value ||
          selectOptions[0].value
      );
      onCategoryChange();
      setSearchValue('');
      setCurrentPage(1);
    };

  const listItemsMapFN = useCallback(
    item => {
      const { name, subtitle, customerId, region, icon, ...rest } = item.props;
      return {
        ...rest,
        searchWords: [item.name, item.subtitle],
        textNode: (
          <ResultItem
            name={name}
            subtitle={subtitle}
            searchText={searchValue}
            customerId={customerId}
            icon={icon}
            region={region}
          />
        )
      };
    },
    [searchValue]
  );

  const debouncedHandleTextChange = useMemo(() => {
    return debounce((selectedCategory, value, page = 1, limit = pageSize) => {
      handleTextChange(selectedCategory, value, page, limit);
    }, 700);
  }, [handleTextChange]);

  const handleTextChangeCB = useCallback(
    (
      value:
        | string
        | React.SyntheticEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      const searchValue =
        typeof value === 'string'
          ? value
          : (value.target as HTMLInputElement).value;
      setSearchValue(searchValue);
      setCurrentPage(1);

      if (searchValue.length >= MIN_CHARACTER_LENGTH) {
        setHasSearched(true);
        setIsPendingSearch(true);
      } else {
        setHasSearched(false);
        setIsPendingSearch(false);
      }

      debouncedHandleTextChange(selectValue, searchValue, 1, pageSize);
    },
    [selectValue, debouncedHandleTextChange]
  );

  const fetchMoreData = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    handleTextChange(selectValue, searchValue, nextPage, pageSize);
  };

  const filterOptions = (
    options: AutocompleteOptionProps[],
    input: { inputValue: string }
  ) => {
    const { inputValue } = input;
    const lowerInput = inputValue.toLowerCase();
    return options.filter(
      option =>
        String(option.name).toLowerCase().includes(lowerInput) ||
        (Array.isArray(option.subtitle)
          ? option.subtitle.some(sub => sub.toLowerCase().includes(lowerInput))
          : option.subtitle?.toLowerCase().includes(lowerInput))
    );
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutocompleteOptionProps
  ) => (
    <ResultItem
      {...props}
      name={String(option.name)}
      subtitle={option.subtitle || ''}
      searchText={searchValue}
      customerId={(option as ResultItemProps).customerId}
      icon={(option as ResultItemProps).icon}
      region={(option as ResultItemProps).region}
    />
  );

  const shouldShowNoResults = useCallback(() => {
    return (
      searchValue.length >= MIN_CHARACTER_LENGTH &&
      !results.length &&
      !loading &&
      hasSearched &&
      !isPendingSearch
    );
  }, [searchValue, results.length, loading, hasSearched, isPendingSearch]);

  return (
    <div className={classes.container}>
      <Select
        value={selectValue}
        classes={classes}
        menuContentProps={{
          handleClick: handleSelectChange,
          listItems: selectOptions
        }}
        MenuProps={{
          disableScrollLock: true
        }}
      />
      <Divider orientation="vertical" flexItem />
      <Autocomplete
        menuContentProps={{
          menuListHeader: (
            <Grid flexDirection="row" container p={1.8}>
              <Typography fontWeight="medium">
                {t('globalSearch:results')}
              </Typography>
              <Typography className={classes.highlight} fontWeight="medium">
                ({totalRecords || results?.length})
              </Typography>
            </Grid>
          ),
          infinityScrollProps: {
            allItemsCount: totalRecords,
            fetchFunction: fetchMoreData,
            threshold: 3
          }
        }}
        disableClearable
        classes={{
          ...classes,
          popper: `${classes.popper} ${
            loading || shouldShowNoResults() ? classes.noOptionsPopper : ''
          }`
        }}
        value={searchValue}
        handleTextChangeCB={handleTextChangeCB}
        options={results}
        placeholder={setSearchPlaceholder(selectValue)}
        noOptionsText={
          shouldShowNoResults() && <NoResults input={searchValue} />
        }
        listItemsMapFN={listItemsMapFN}
        filterOptions={filterOptions}
        renderOption={renderOption}
        loading={loading}
      />
    </div>
  );
};
